import { Component, Element, h, Host, State } from "@stencil/core";
import { debounce } from "../../../../utils/debounce";

const baseClass = "ux-iws-sticky-scroll-bar";

@Component({
    tag: "ux-iws-sticky-scroll-bar",
    styleUrl: "sticky-scroll-bar.scss",
    shadow: false,
})
export class UxIwsStickyScrollBar {
    @Element() host: HTMLUxIwsStickyScrollBarElement;
    /** width of the scrollbar: 0 to 1 */
    @State() width = 0;
    /** scroll of the scrollbar: 0 to 1 */
    @State() scroll = 0;
    private isDragging = false;
    private scrollElement: HTMLElement;
    private hostWidth = 0;
    private scrollWidth = 0;
    private eventMouseMove;
    private eventMouseUp;

    componentDidLoad() {
        this.scrollElement = this.host
            .closest("ux-iws-stateful-table")
            .querySelector(".ux-table__inner");
        if (this.scrollElement !== null) {
            this.initMethods();
        } else {
            this.checkForTable();
        }
    }

    private bodyScrollLock(isLocked) {
        if (isLocked) {
            document.body.classList.add("ux-body-scroll-lock");
        } else {
            document.body.classList.remove("ux-body-scroll-lock");
        }
    }

    private checkForTable = () => {
        const targetNode = document.body;
        const observerConfig = { childList: true, subtree: true };
        const mutationCallback = (mutationsList, observer) => {
            for (const mutation of mutationsList) {
                if (
                    mutation.type === "childList" &&
                    mutation.addedNodes.length > 0
                ) {
                    for (const addedNode of mutation.addedNodes) {
                        if (
                            addedNode.nodeType === 1 &&
                            addedNode.querySelector(".ux-table__inner") &&
                            addedNode.querySelector("table")
                        ) {
                            const uxTableInnerNode = addedNode.querySelector(
                                ".ux-table__inner"
                            );
                            this.scrollElement = uxTableInnerNode;
                            this.initMethods();
                            observer.disconnect();
                            return;
                        }
                    }
                }
            }
        };
        const observer = new MutationObserver(mutationCallback);
        observer.observe(targetNode, observerConfig);
    };

    private initMethods = () => {
        this.updateSizes();
        this.addScrollListener();
        this.addResizeListener();
        this.updateSizesDebounced();
        this.checkWindowScrollPosition();
    };

    private updateSizes() {
        this.hostWidth = this.host.clientWidth;
        this.scrollWidth = this.scrollElement.scrollWidth;
        this.width = this.hostWidth / this.scrollWidth;
    }

    private updateSizesDebounced = debounce(() => {
        this.updateSizes();
    }, 100);

    private addResizeListener = debounce(() => {
        window.addEventListener("resize", this.updateSizes.bind(this));
    }, 100);

    private addScrollListener = debounce(() => {
        this.scrollElement.addEventListener(
            "scroll",
            () => {
                const setScroll = () => {
                    this.scroll =
                        this.scrollElement.scrollLeft / this.scrollWidth;
                };

                requestAnimationFrame(() => {
                    setScroll();
                });
            },
            { passive: true }
        );
    }, 100);

    private handleDragStart() {
        this.isDragging = true;
        this.host.style.cursor = "grabbing";
        this.eventMouseMove = this.handleDragMove.bind(this);
        this.eventMouseUp = this.handleDragEnd.bind(this);
        document.addEventListener("mousemove", this.eventMouseMove);
        document.addEventListener("mouseup", this.eventMouseUp);
        this.bodyScrollLock(true);
    }
    private handleDragMove(e) {
        if (!this.isDragging) {
            return;
        }
        const scrollRelation = e.movementX / this.host.clientWidth;
        this.scrollElement.scrollLeft =
            this.scrollElement.scrollLeft + scrollRelation * this.scrollWidth;
    }
    private handleDragEnd() {
        this.isDragging = false;
        this.host.style.cursor = "auto";
        document.removeEventListener("mousemove", this.eventMouseMove);
        document.removeEventListener("mouseup", this.eventMouseUp);
        this.bodyScrollLock(false);
    }

    private checkWindowScrollPosition() {
        window.addEventListener("scroll", () => {
            var rect = this.host.getBoundingClientRect();
            if (rect.top <= 0) {
                this.host.classList.add("is-sticky");
            } else {
                this.host.classList.remove("is-sticky");
            }
        });
    }

    private getScrollBar() {
        return (
            <div
                class={`${baseClass}__track-container`}
                style={{
                    display: this.width >= 1 ? "none" : "block",
                }}
            >
                <div
                    class={`${baseClass}__track`}
                    style={{
                        marginLeft: `${this.scroll * 100}%`,
                        width: `${this.width * 100}%`,
                    }}
                    onMouseDown={this.handleDragStart.bind(this)}
                ></div>
            </div>
        );
    }

    render() {
        return <Host class={baseClass}>{this.getScrollBar()}</Host>;
    }
}
